import {
  useEffect, useId, useRef, useState,
} from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import FormHelperText from '@material-ui/core/FormHelperText'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Controller } from 'react-hook-form'
import Recaptcha from '@components/recaptcha'
import MarketingOptIn, { MarketingOptInTermsNotice } from '@components/marketing-opt-in'
import EmailFieldWithSuggestions from '@components/form-utils/email-field-with-suggestions'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import {
  Check,
  ChevronRight,
  CircleCheck,
  COACH_PROFILE_INLINE_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import useCoachingLeadForm from './use-coaching-lead-form'
import { useSidebarStyles } from './sidebar-styles'

export const SIDEBAR_LEAD_FORM_SUBTITLE = 'Send a request to start a conversation.'

/**
 * The sidebar's four most-picked goals, in a fixed order. `value` is the
 * existing `intent` string the lead API stores and coaches already see,
 * so short labels do not split lead reporting.
 */
export const SIDEBAR_INTENT_OPTIONS = [
  { label: 'Lower my scores', value: 'Help me shoot lower scores' },
  { label: 'Fix a specific problem in my golf game', value: 'Fix a specific problem in my golf game' },
  { label: 'Teach me the basics', value: 'Introduce me to golf' },
  { label: 'Work with my junior golfer', value: 'Help my junior golfer improve' },
]

const intentLabel = (value) => (
  SIDEBAR_INTENT_OPTIONS.find((option) => option.value === value)?.label ?? value
)

const PHONE_DISALLOWED_CHARS = /[^\d()\-. ]/g

export function formatUsPhoneNumber(value) {
  const allDigits = value.replace(/\D/g, '')
  const digits = allDigits.length === 11 && allDigits.startsWith('1')
    ? allDigits.slice(1)
    : allDigits
  if (digits.length !== 10) return value
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * Compact Ask the Coach card for the flagged profile's md+ sidebar.
 * Step 1 is a single tap on what the visitor wants help with; step 2
 * collects contact details, consent, and reCAPTCHA. Both steps stay
 * mounted so react-hook-form keeps every value, and the inactive step
 * uses the `hidden` attribute so it is out of the tab order and the
 * accessibility tree.
 */
function SidebarLeadForm({ coach, alreadySubmitted = false, onSubmitted }) {
  const classes = useSidebarStyles()
  const theme = useTheme()
  /** The 304px reCAPTCHA only fits the 1/3 column from `lg`. */
  const recaptchaSize = useMediaQuery(theme.breakpoints.up('lg')) ? 'normal' : 'compact'
  /** The sidebar column is `display: none` below `md`, so the card is only seen from `md`. */
  const sidebarVisible = useMediaQuery(theme.breakpoints.up('md'))
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const {
    form: {
      register, control, handleSubmit, errors, watch,
    },
    coachName,
    submitted,
    submittedHere,
    onSubmit,
    submitErrorMessage,
    shouldMountRecaptcha,
    setRecaptchaToken,
    marketingOptInChecked,
    setMarketingOptInChecked,
    submitDisabled,
  } = useCoachingLeadForm({
    coach,
    position: 'sidebar',
    marketingOptInVariant: 'standard',
    alreadySubmitted,
    onSubmitted,
  })

  const headingId = useId()
  const [step, setStep] = useState(1)
  const [reachedDetails, setReachedDetails] = useState(false)
  const [recaptchaExpired, setRecaptchaExpired] = useState(false)
  const moveFocusOnStepChange = useRef(false)
  const intentStepRef = useRef(null)
  const detailsStepRef = useRef(null)
  const successHeadingRef = useRef(null)

  const selectedIntent = watch('interest')
  const coachFirstName = coach.firstName || 'this coach'

  useEffect(() => {
    if (!moveFocusOnStepChange.current) return
    moveFocusOnStepChange.current = false
    const target = step === 2
      ? detailsStepRef.current?.querySelector('input')
      : intentStepRef.current?.querySelector('[aria-pressed="true"]')
    target?.focus()
  }, [step])

  useEffect(() => {
    if (submittedHere) successHeadingRef.current?.focus()
  }, [submittedHere])

  useEffect(() => {
    setRecaptchaToken(null)
  }, [recaptchaSize, setRecaptchaToken])

  const impressionTrackedRef = useRef(false)
  useEffect(() => {
    if (!sidebarVisible || submitted || impressionTrackedRef.current) return
    impressionTrackedRef.current = true
    trackCoachProfileEvent('view-coach-profile-lead-form', { position: 'sidebar' })
  }, [sidebarVisible, submitted, trackCoachProfileEvent])

  const goToStep = (nextStep) => {
    moveFocusOnStepChange.current = true
    setStep(nextStep)
  }

  const chooseIntent = (intent, onChange) => {
    onChange(intent)
    trackCoachProfileEvent('select-coach-profile-lead-intent', {
      lead_intent: intent,
      position: 'sidebar',
    })
    setReachedDetails(true)
    goToStep(2)
  }

  const onInvalid = (invalidFields) => {
    if (invalidFields.interest) goToStep(1)
  }

  if (submitted) {
    return (
      <section className={classes.root} data-testid="lead-form" aria-labelledby={headingId}>
        <div className={classes.success} role="status">
          <CircleCheck size={40} strokeWidth={1.75} aria-hidden className={classes.successIcon} />
          <Typography
            id={headingId}
            ref={successHeadingRef}
            tabIndex={-1}
            variant="h5"
            component="h2"
            className={classes.heading}
          >
            Thanks for your interest!
          </Typography>
          <Typography component="p" className={classes.subtitle}>
            Your information has been shared with Coach {coach.firstName || coachName}.
            You will also receive an email confirmation.
          </Typography>
        </div>
      </section>
    )
  }

  return (
    <section
      className={classes.root}
      data-testid="lead-form"
      data-lead-form-width="sidebar"
      aria-labelledby={headingId}
    >
      <div className={classes.header}>
        <Typography id={headingId} variant="h5" component="h2" className={classes.heading}>
          Ask the Coach
        </Typography>
        <Typography component="p" className={classes.stepLabel} aria-live="polite">
          {`Step ${step} of 2`}
        </Typography>
      </div>
      <div className={classes.progress} aria-hidden>
        <span className={`${classes.progressSegment} ${classes.progressSegmentActive}`} />
        <span className={step === 2
          ? `${classes.progressSegment} ${classes.progressSegmentActive}`
          : classes.progressSegment}
        />
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <div
          ref={intentStepRef}
          hidden={step !== 1}
          className={classes.step}
          data-testid="ask-the-coach-step-intent"
        >
          <Typography component="p" className={classes.subtitle}>
            {SIDEBAR_LEAD_FORM_SUBTITLE}
          </Typography>
          <Controller
            name="interest"
            control={control}
            defaultValue=""
            render={({ value, onChange }) => (
              <fieldset className={classes.fieldset}>
                <legend className={classes.question}>
                  {`What can ${coachFirstName} help you with?`}
                </legend>
                <ul className={classes.intentList}>
                  {SIDEBAR_INTENT_OPTIONS.map((option) => {
                    const selected = value === option.value
                    return (
                      <li key={option.value}>
                        <button
                          type="button"
                          aria-pressed={selected}
                          className={selected
                            ? `${classes.intentOption} ${classes.intentOptionSelected}`
                            : classes.intentOption}
                          onClick={() => chooseIntent(option.value, onChange)}
                        >
                          <span>{option.label}</span>
                          {selected
                            ? <Check {...COACH_PROFILE_INLINE_ICON_PROPS} />
                            : <ChevronRight {...COACH_PROFILE_INLINE_ICON_PROPS} />}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </fieldset>
            )}
          />
          {errors.interest ? (
            <FormHelperText error>{errors.interest.message}</FormHelperText>
          ) : null}
        </div>

        <div
          ref={detailsStepRef}
          hidden={step !== 2}
          className={classes.step}
          data-testid="ask-the-coach-step-details"
        >
          <div className={classes.selection}>
            <div className={classes.selectionText}>
              <span className={classes.selectionLabel}>Interested in</span>
              <span className={classes.selectionValue}>{intentLabel(selectedIntent)}</span>
            </div>
            <Button
              size="small"
              className={classes.changeButton}
              type="button"
              aria-label="Change interest"
              onClick={() => goToStep(1)}
            >
              Change
            </Button>
          </div>

          <div className={classes.fields}>
            <div className={classes.nameRow}>
              <TextField
                id="ask-the-coach-first-name"
                name="firstName"
                label="First name"
                required
                fullWidth
                variant="outlined"
                autoComplete="given-name"
                className={classes.field}
                inputRef={register}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
              <TextField
                id="ask-the-coach-last-name"
                name="lastName"
                label="Last name"
                required
                fullWidth
                variant="outlined"
                autoComplete="family-name"
                className={classes.field}
                inputRef={register}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </div>
            <EmailFieldWithSuggestions
              id="ask-the-coach-email"
              fieldName="email"
              control={control}
              errors={errors}
              label="Email"
              variant="outlined"
              className={classes.field}
              fullWidth
              required
            />
            <TextField
              id="ask-the-coach-phone"
              name="phoneNumber"
              label="Phone number"
              type="tel"
              required
              fullWidth
              variant="outlined"
              autoComplete="tel-national"
              className={classes.field}
              inputRef={register}
              inputProps={{ maxLength: 17 }}
              onChange={(e) => {
                e.target.value = e.target.value.replace(PHONE_DISALLOWED_CHARS, '')
              }}
              onBlur={(e) => {
                e.target.value = formatUsPhoneNumber(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.value = formatUsPhoneNumber(e.target.value)
              }}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
            />
          </div>

          <MarketingOptIn
            checked={marketingOptInChecked}
            onChange={setMarketingOptInChecked}
            useMarketingOptIn="standard"
            showTermsNotice={false}
          />

          {reachedDetails && shouldMountRecaptcha ? (
            <div className={classes.recaptcha}>
              <Recaptcha
                key={recaptchaSize}
                size={recaptchaSize}
                setToken={(token) => {
                  setRecaptchaToken(token)
                  if (token) setRecaptchaExpired(false)
                }}
                resetOnExpire
                onExpired={() => setRecaptchaExpired(true)}
              />
              {recaptchaExpired ? (
                <FormHelperText error className={classes.recaptchaExpired}>
                  Verification expired. Check the box again.
                </FormHelperText>
              ) : null}
            </div>
          ) : null}
          <MarketingOptInTermsNotice className={classes.termsNotice} />
          {submitErrorMessage ? (
            <FormHelperText component="div" error className={classes.submitError}>
              {submitErrorMessage}
            </FormHelperText>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={submitDisabled}
            className={classes.submit}
          >
            Send Request
          </Button>
        </div>
      </form>
    </section>
  )
}

SidebarLeadForm.propTypes = {
  coach: PropTypes.shape({
    customSlug: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    displayName: PropTypes.string,
  }).isRequired,
  alreadySubmitted: PropTypes.bool,
  onSubmitted: PropTypes.func,
}

SidebarLeadForm.defaultProps = {
  alreadySubmitted: false,
  onSubmitted: undefined,
}

export default SidebarLeadForm
